/**
 * React component that is responsible for rendering tags list
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, ButtonGroup } from 'reactstrap'
import styles from './TagList.module.scss'

const TagBtnGroup = ({ tagName, editable, onDelete, color, size }) => (
  <ButtonGroup className={cx('tag', styles['tag-btn-group'])} size='sm'>
    <Button
      color={color}
      size={size}>#{tagName}
    </Button>
    {(editable) ? (
      <Button
        color={color}
        size={size}
        onClick={onDelete}>
        <FontAwesomeIcon icon={['fal', 'times']} className='font-inherit' />
      </Button>
    ) : null}
  </ButtonGroup>
)

TagBtnGroup.propTypes = {
  tagName: PropTypes.string,
  editable: PropTypes.bool,
  onDelete: PropTypes.func,
  color: PropTypes.string,
  size: PropTypes.string
}

const TagList = ({ className, children, tagOrder, editable, onDelete, color, size, limit }) => {
  const Tags = useMemo(() => {
    return tagOrder.slice(0, limit || tagOrder.length).map((tagName) => {
      const handleDelete = () => {
        onDelete(tagName)
      }

      return (color === 'naked') ? (
        <span key={`tag-${tagName}`} className={styles['naked']}>#{tagName}</span>
      ) : (
        <TagBtnGroup
          key={`tag-${tagName}`}
          tagName={tagName}
          editable={editable}
          onDelete={handleDelete}
          color={color}
          size={size} />
      )
    })
  }, [tagOrder, editable, onDelete, color, size, limit])

  return (
    <div className={cx(styles['tags-container'], className)}>
      {Tags}
      {(children) ? (
        <div className={cx(styles['children-wrapper'])}>
          {children}
        </div>
      ) : null}
    </div>
  )
}

TagList.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  tagOrder: PropTypes.array,
  editable: PropTypes.bool,
  onDelete: PropTypes.func,
  color: PropTypes.oneOf(['tag', 'tag-pro', 'naked']),
  size: PropTypes.string,
  limit: PropTypes.number
}

TagList.defaultProps = {
  tagOrder: [],
  editable: false,
  onDelete: () => false,
  color: 'tag',
  size: 'sm'
}

export default TagList
